import { ISavedView } from '@opengov/reporting-types';
import { createSelector } from 'reselect';
import { getCurrentPathname } from '@opengov/compose-app-router/selectors';
import { selectPathname } from '../../application/common/report/selectors/pathnameSelectors';
import { isEmbeddedFromLocalStorageAndTopUrl } from '@opengov/app-core/utils/embedded';
export const rootPath = `/reporting`;

export const transparencyRootPath = `/transparency-reporting`;

export const baseReportsV2ServiceUrl = '/api/reporting_service/v2';

export const baseReportsV3ServiceUrl = '/api/reporting_service/v3';

export const selectCurrentPathname = createSelector(getCurrentPathname, selectPathname, (currentPathname, pathname) => {
  return currentPathname || pathname;
});

export const selectIsTransparency = createSelector(selectCurrentPathname, (path) =>
  path.includes('transparency-reporting')
);
export const isEmbeddedFromUrl = (): boolean => isEmbeddedFromLocalStorageAndTopUrl();

export const embeddedQuery = (hasExistingQuery: boolean, embedded?: boolean): string => {
  const shouldEmbed = embedded ?? isEmbeddedFromUrl();
  if (!shouldEmbed) {
    return '';
  }
  return hasExistingQuery ? '&embedded=true' : '?embedded=true';
};

export const multiDatasetReportEditPath = (
  entitySubdomain: string,
  reportId: string,
  reportConfigurationId: string,
  savedViewId: string,
  embedded?: boolean
): string =>
  `${rootPath}/${entitySubdomain}/${reportId}/${reportConfigurationId}/edit?savedViewId=${savedViewId}${embeddedQuery(
    true,
    embedded
  )}`;

export const multiDatasetReportEditPathReactRouterVersion = (
  entitySubdomain: string,
  reportId: string,
  reportConfigurationId: string,
  savedViewId: string,
  embedded?: boolean
): string =>
  `/${entitySubdomain}/${reportId}/${reportConfigurationId}/edit?savedViewId=${savedViewId}${embeddedQuery(
    true,
    embedded
  )}`;

export const multiDatasetReportViewPath = (
  entitySubdomain: string,
  reportId: string,
  reportConfigurationId: string,
  savedViewId: string,
  embedded?: boolean
): string =>
  `${rootPath}/${entitySubdomain}/${reportId}/${reportConfigurationId}?savedViewId=${savedViewId}${embeddedQuery(
    true,
    embedded
  )}`;

export const multiDatasetReportViewPathReactRouterVersion = (
  entitySubdomain: string,
  reportId: string,
  reportConfigurationId: string,
  savedViewId?: string,
  embedded?: boolean
): string => {
  const savedViewQuery = savedViewId ? `?savedViewId=${savedViewId}` : '';
  return `/${entitySubdomain}/${reportId}/${reportConfigurationId}${savedViewQuery}${embeddedQuery(
    Boolean(savedViewId),
    embedded
  )}`;
};

export const accountSummaryMultiDatasetReportViewPath = (
  entitySubdomain: string,
  reportId: string,
  reportConfigurationId: string,
  savedViewId?: string,
  embedded?: boolean
): string => {
  const basePath = `/${entitySubdomain}/accountSummary/${reportId}/${reportConfigurationId}`;
  const withSavedView = savedViewId ? `${basePath}?savedViewId=${savedViewId}` : basePath;
  return `${withSavedView}${embeddedQuery(Boolean(savedViewId), embedded)}`;
};

export const reportGalleryPath = (entitySubdomain: string, embedded?: boolean): string =>
  `${rootPath}/${entitySubdomain}${embeddedQuery(false, embedded)}`;
export const reportGalleryPathReactRouterVersion = (entitySubdomain: string, embedded?: boolean): string =>
  `/${entitySubdomain}${embeddedQuery(false, embedded)}`;

export const embedTestPath = (entitySubdomain: string, embedded?: boolean): string =>
  `${rootPath}/${entitySubdomain}/embedTest?reportId=&savedViewId=&summaryTable=${embeddedQuery(true, embedded)}`;

export const reportCreatePath = (entitySubdomain: string, embedded?: boolean): string =>
  `/${entitySubdomain}/create${embeddedQuery(false, embedded)}`;

export const reportBuilderPath = (entitySubdomain: string): string => `/${entitySubdomain}/report-builder`;

export const standardReportPath = (
  entitySubdomain: string,
  templateId: string,
  queryString: string,
  embedded?: boolean
): string => `/${entitySubdomain}/standard/${templateId}?${queryString}${embeddedQuery(true, embedded)}`;

export const standardReportPromptsPath = (entitySubdomain: string, templateId: string, embedded?: boolean): string =>
  `/${entitySubdomain}/standard/${templateId}/prompts${embeddedQuery(false, embedded)}`;

export const reportSavedViewsPath = (
  entitySubdomain: string,
  {
    reportId,
    reportConfigurationId,
    savedViewId
  }: {
    reportId: string;
    reportConfigurationId?: string;
    savedViewId?: string;
  }
): string =>
  makeReportExplorerPath({ isTransparency: false })(entitySubdomain, reportId, reportConfigurationId, savedViewId);

export const reportSavedViewsPathReactRouterVersion = (
  entitySubdomain: string,
  {
    reportId,
    reportConfigurationId,
    savedViewId
  }: {
    reportId: string;
    reportConfigurationId?: string;
    savedViewId?: string;
  }
): string => makeReportExplorerPathReactRouterVersion(entitySubdomain, reportId, reportConfigurationId, savedViewId);

export const reportSavedViewEditPath = (
  entitySubdomain: string,
  reportId: string,
  viewId: string,
  embedded?: boolean
): string => {
  const query = embeddedQuery(false, embedded);
  return `${rootPath}/${entitySubdomain}/${reportId}/views/${viewId}/edit${query}`;
};

export const reportSavedViewEditPathReactRouterVersion = (
  entitySubdomain: string,
  reportId: string,
  viewId: string,
  embedded?: boolean
): string => `/${entitySubdomain}/${reportId}/views/${viewId}/edit${embeddedQuery(false, embedded)}`;

export const reportSettingsPath = (entitySubdomain: string, reportId: string, embedded?: boolean): string =>
  `${rootPath}/${entitySubdomain}/${reportId}/settings${embeddedQuery(false, embedded)}`;

export const reportSettingsPathReactRouterVersion = (
  entitySubdomain: string,
  reportId: string,
  embedded?: boolean
): string => `/${entitySubdomain}/${reportId}/settings${embeddedQuery(false, embedded)}`;

export const reportCDSettingsPathReactRouterVersion = (
  entitySubdomain: string,
  reportId: string,
  embedded?: boolean
): string => `/${entitySubdomain}/${reportId}/chart-and-details/settings${embeddedQuery(false, embedded)}`;

export const standardReportSettingsPath = (entitySubdomain: string, templateId: string, embedded?: boolean): string =>
  `/${entitySubdomain}/standard/${templateId}/settings${embeddedQuery(false, embedded)}`;

export const reportSavedViewEditPathWithConfiguration = (
  entitySubdomain: string,
  reportId: string,
  viewId: string,
  reportConfigurationId: string,
  embedded?: boolean
): string => {
  const query = embeddedQuery(true, embedded);
  return `${rootPath}/${entitySubdomain}/${reportId}/views/${viewId}/edit?reportConfigurationId=${reportConfigurationId}${query}`;
};

export const reportSavedViewEditPathWithConfigurationReactRouterVersion = (
  entitySubdomain: string,
  reportId: string,
  viewId: string,
  reportConfigurationId: string,
  embedded?: boolean
): string =>
  `/${entitySubdomain}/${reportId}/views/${viewId}/edit?reportConfigurationId=${reportConfigurationId}${embeddedQuery(
    true,
    embedded
  )}`;

export const reportExplorerSuffix = (entitySubdomain: string, reportId: string, embedded?: boolean): string =>
  `/${entitySubdomain}/${reportId}${embeddedQuery(false, embedded)}`;

export const reportViewConfigurationSuffix = (
  entitySubdomain: string,
  reportId: string,
  configId: string,
  embedded?: boolean
): string => {
  const query = embeddedQuery(false, embedded);
  return `/${entitySubdomain}/${reportId}/${configId}${query}`;
};

export const reportViewConfigurationSavedViewSuffix = (
  entitySubdomain: string,
  reportId: string,
  configId: string,
  savedViewId: string,
  embedded?: boolean
): string => `/${entitySubdomain}/${reportId}/${configId}?savedViewId=${savedViewId}${embeddedQuery(true, embedded)}`;

export const reportSavedViewSuffix = (
  entitySubdomain: string,
  reportConfigurationId: string,
  savedView: ISavedView,
  embedded?: boolean
): string => {
  const query = embeddedQuery(true, embedded);
  return `/${entitySubdomain}/${savedView.reportId}/${reportConfigurationId}?savedViewId=${savedView.id ?? ''}${query}`;
};

export const makeReportSavedViewPath =
  (root: { isTransparency: boolean }) =>
  (entitySubdomain: string, reportConfigurationId: string, savedView: ISavedView, embedded?: boolean): string =>
    `${root.isTransparency ? transparencyRootPath : rootPath}${reportSavedViewSuffix(
      entitySubdomain,
      reportConfigurationId,
      savedView,
      embedded
    )}`;

export const selectSavedViewPath = createSelector(selectIsTransparency, (isTransparency) =>
  makeReportSavedViewPath({ isTransparency })
);

export const makeReportExplorerPath =
  (root: { isTransparency: boolean }) =>
  (entitySubdomain: string, reportId: string, reportConfigId?: string, savedViewId?: string): string => {
    // select base
    let base = rootPath;
    if (root.isTransparency) {
      base = transparencyRootPath;
    }
    // create suffix
    let suffix = reportExplorerSuffix(entitySubdomain, reportId);
    if (reportConfigId) {
      suffix = reportViewConfigurationSuffix(entitySubdomain, reportId, reportConfigId);
      if (savedViewId) {
        suffix = reportViewConfigurationSavedViewSuffix(entitySubdomain, reportId, reportConfigId, savedViewId);
      }
    }
    return `${base}${suffix}`;
  };

export const makeReportExplorerPathReactRouterVersion = (
  entitySubdomain: string,
  reportId: string,
  reportConfigId?: string,
  savedViewId?: string
): string => {
  let suffix = reportExplorerSuffix(entitySubdomain, reportId);
  if (reportConfigId) {
    suffix = reportViewConfigurationSuffix(entitySubdomain, reportId, reportConfigId);
    if (savedViewId) {
      suffix = reportViewConfigurationSavedViewSuffix(entitySubdomain, reportId, reportConfigId, savedViewId);
    }
  }
  return suffix;
};

export const selectReportExplorerPath = createSelector(selectIsTransparency, (isTransparency) =>
  makeReportExplorerPath({ isTransparency })
);

export const makeReportViewConfigurationPath =
  (root: { isTransparency: boolean }) =>
  (entitySubdomain: string, reportId: string, configId: string, embedded?: boolean): string => {
    return `${root.isTransparency ? transparencyRootPath : rootPath}${reportViewConfigurationSuffix(
      entitySubdomain,
      reportId,
      configId,
      embedded
    )}`;
  };

export const selectReportViewConfigurationPath = createSelector(selectIsTransparency, (isTransparency) => {
  return makeReportViewConfigurationPath({ isTransparency });
});
